/* global fetchFinancialSummaryUrl lockedBudgets agencyMode marginDisplayBasis */

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import { range } from 'lodash';

import KeyStatTileSkeleton from '../KeyStatTileSkeleton';

import { formatFigure, formatOverviewFigure, formatOverviewVariance, formatToFixedMoneyWithLocale } from 'Helpers/formatService';
import { marginPercentage } from 'Helpers/marginPercentage';
import { useDisplayUnit } from 'Helpers/overviewDisplayUnit';

import KeyStatTile from 'CommonComponents/KeyStatTile';

import MinusCircleIcon from 'Assets/icons/minus.svg';
import PlusCircleIcon from 'Assets/icons/plus.svg';

import 'react-loading-skeleton/dist/skeleton.css';

// The unit-aware money cell for the previous-budget comparison sub-table. The
// variance (%) row is NOT money and keeps its own formatting.
function ComparisonMoneyCell({ raw, unit }) {
    const { text, title } = formatOverviewFigure(raw, unit);

    return (
        <td className="inbud-numeric-cell" title={title}>
            {text}
        </td>
    );
}

export default function KeyMetricsFinancialSummary(props) {
    const [show, setShow] = useState(true);
    const [budgets, setBudgets] = useState(props.lockedBudgets);
    const [prevBudgetId, setPrevBudgetId] = useState(null);
    const [summaryFinancial, setSummaryFinancial] = useState({});
    const [previousBudget, setPreviousBudget] = useState(null);
    const [fetched, setFetched] = useState(false);
    const unit = useDisplayUnit();

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (fetched) {
            fetchData();
        }
    }, [prevBudgetId]);

    const changeHandler = (event) => {
        setPrevBudgetId(event.target.value);
    };

    const fetchData = () => {
        var axiosPromise = axios
            .get(props.fetchUrl, {
                params: {
                    prev_budget_id: prevBudgetId,
                },
            })
            .then((response) => {
                const { financialSummary, previousBudget } = response.data;
                setSummaryFinancial(financialSummary);
                setPreviousBudget({ ...previousBudget });

                if (!prevBudgetId) {
                    setPrevBudgetId(previousBudget.id);
                }
            })
            .catch((e) => {})
            .finally(() => {
                if (!fetched) {
                    setFetched(true);
                }
            });
        // loaderInstance.trackAxiosRequest(axiosPromise);
    };

    // Unit note shown beneath the title, mirroring the unit the figures render in.
    const unitNote = unit === 'k' ? $t('figures in thousands') : unit === 'full' ? $t('full figures') : $t('figures in millions');

    // Formats a raw figure into KeyStatTile's { value, valueTitle } (text + hover).
    // A tile always shows a figure: a missing value reads as zero, so it takes the
    // dash rather than the blank a table cell gets.
    const cardValue = (raw) => {
        const n = Number(raw);
        const { text, title } = formatOverviewFigure(isFinite(n) ? n : 0, unit);
        return { value: text, valueTitle: title };
    };

    // Formats a raw variance into KeyStatTile's { percentage, percentageLabel }: the
    // numeric drives the arrow direction and its colour, the label is the text that
    // renders. A falsy variance passes a non-number, which is how KeyStatTile
    // suppresses the badge entirely.
    //
    // `reversed` tiles (Expenses, Risks) get a variance the backend already sign-flips
    // so a bad movement reads as negative (DashboardService::calculateVariancePercentage,
    // $negative = true) — that flip is what drives the correct arrow/colour below, via
    // KeyStatTile's own `reversed` prop. But it is not the real percentage change, so
    // bracketing it as-is reports an expense INCREASE as "(12.5)" — parentheses
    // reading as a decrease. The label un-flips back to the real change before
    // formatting; `percentage` stays flipped, since the arrow/colour logic needs it as-is.
    const cardPercentage = (raw, reversed = false) =>
        raw ? { percentage: Number(Number(raw).toFixed(1)), percentageLabel: formatOverviewVariance(reversed ? -raw : raw) } : { percentage: '' };

    // Agency mode replaces all six tiles with one tier of four — Expenses,
    // Mark-ups, Total, Margin % — the same set, in the same order, as the
    // department / cost-centre and Expenditure summary pages. The figures come from
    // the response's `agency` block: financial_stats carries no mark-up.
    const agency = summaryFinancial.agency;

    // Revenue is the one conditional tile, and only here: an agency budget usually
    // has no revenue side at all, and a Revenue tile reading nil on every such
    // project is noise. Gated on revenue LINES existing rather than on a non-zero
    // total, so a budget carrying a nil-value revenue line still reports one.
    // Display only — it is not part of Total and never feeds Margin %.
    const showRevenue = Boolean(agency?.has_revenue_lines);

    // Through the shared helper, so this tile quotes the same figure, on the same
    // dash rule and to the same precision, as the Margin % on the three report
    // screens it summarises. Mark-ups over cost, or over cost + mark-ups; revenue
    // is not a term in either.
    const marginLabel = () => marginPercentage(agency?.current?.total_markup, agency?.current?.total_cost, props.marginDisplayBasis, { zeroDash: false });

    // Net / Risks / Opportunities / AFC leave the previous-budget comparison table in
    // agency mode. The Overview table's bottom row is the only Net agency mode shows,
    // so a Net column here would put a second one on the same page — and one computed
    // the non-agency way, against cost that includes workforce and excludes mark-up.
    // The other three leave for the same reason they leave the tiles and the Overview
    // columns. Revenue and Expenditure stay: they are what a version-to-version
    // comparison is read for.
    const showComparisonExtras = !props.agencyMode;

    return (
        <div className="table__card">
            <div className="tw-flex tw-flex-row tw-justify-between tw-pb-3">
                <div>
                    <h3 className="tw-text-blue tw-text-2xl tw-font-semibold">{$t('Financial Summary')}</h3>
                    <div className="tw-text-xs tw-text-charcoal-50 tw-font-normal">{unitNote}</div>
                </div>
                <div className={'tw-flex tw-flex-row tw-gap-x-1'}>
                    {!!previousBudget && !!prevBudgetId && (
                        <div className="tw-flex tw-flex-row tw-items-center tw-gap-1">
                            <label className={'tw-m-0'}>{$t('Previous budget')}</label>
                            <div>
                                <select
                                    className={'tw-border-blue tw-border-solid tw-border-2 tw-rounded-full tw-text-base tw-p-1 tw-px-3'}
                                    value={prevBudgetId}
                                    onChange={changeHandler}
                                >
                                    {budgets.map((budget) => {
                                        return (
                                            <option value={budget.id} key={budget.id}>
                                                {budget.name}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        </div>
                    )}
                    {!show && (
                        <div className={'tw-cursor-pointer'} onClick={() => setShow(true)}>
                            <div className={'tw-h-10 tw-w-10 tw-bg-blue tw-flex tw-justify-center tw-items-center tw-rounded-full'}>
                                <PlusCircleIcon className={'tw-h-3.5 tw-w-3.5 tw-text-white'} />
                            </div>
                        </div>
                    )}
                    {!!show && (
                        <div className={'tw-cursor-pointer'} onClick={() => setShow(false)}>
                            <div className={'tw-h-10 tw-w-10 tw-bg-blue tw-flex tw-justify-center tw-items-center tw-rounded-full'}>
                                <MinusCircleIcon className={'tw-h-3.5 tw-w-3.5 tw-text-white'} />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {!!previousBudget && !!prevBudgetId && !!show && (
                <div>
                    <div></div>
                </div>
            )}

            {!fetched || !Object.keys(summaryFinancial).length || !summaryFinancial.current ? (
                /* Placeholder for the tile set that is about to replace it — four in one
                   row in agency mode (the conditional Revenue tile is not yet known,
                   since the response that decides it has not arrived), six in two
                   otherwise. */
                <div className={`tw-grid tw-gap-x-5 tw-gap-y-3 ${props.agencyMode ? 'tw-grid-cols-4' : 'tw-grid-cols-3'}`}>
                    {range(props.agencyMode ? 4 : 6).map((i) => (
                        <KeyStatTileSkeleton key={i} />
                    ))}
                </div>
            ) : (
                !!show && (
                    <div>
                        {props.agencyMode ? (
                            /* Agency mode — one tier of Expenses / Mark-ups / Total / Margin %,
                               with Revenue leading it on the budgets that have any. No Net
                               tile: the Net figure lives in the Overview table alone. */
                            <div className={`tw-grid tw-gap-x-5 tw-gap-y-3 ${showRevenue ? 'tw-grid-cols-5' : 'tw-grid-cols-4'}`}>
                                {showRevenue && (
                                    <KeyStatTile
                                        size="primary"
                                        title={$t('Revenue')}
                                        {...cardValue(summaryFinancial.current.total_revenue)}
                                        {...cardPercentage(summaryFinancial.variance?.total_revenue)}
                                    />
                                )}
                                <KeyStatTile
                                    size="primary"
                                    title={$t('Expenses')}
                                    {...cardValue(agency?.current?.total_cost)}
                                    {...cardPercentage(agency?.variance?.total_cost, true)}
                                    reversed
                                />
                                {/* Not `reversed`, unlike Expenses beside it: for an agency a
                                    rising mark-up — and so a rising total — is the good outcome,
                                    so both badges read the movement straight. */}
                                <KeyStatTile
                                    size="primary"
                                    title={$t('Mark-ups')}
                                    {...cardValue(agency?.current?.total_markup)}
                                    {...cardPercentage(agency?.variance?.total_markup)}
                                />
                                <KeyStatTile
                                    size="primary"
                                    title={$t('Total')}
                                    {...cardValue(agency?.current?.total_client_total)}
                                    {...cardPercentage(agency?.variance?.total_client_total)}
                                />
                                <KeyStatTile size="primary" title={$t('Margin %')} value={marginLabel()} />
                            </div>
                        ) : (
                            <>
                                {/* Primary tier — Revenue / Expenses / Net (larger figures). */}
                                <div className="tw-grid tw-grid-cols-3 tw-gap-x-5 tw-gap-y-3">
                                    <KeyStatTile
                                        size="primary"
                                        title={$t('Revenue')}
                                        {...cardValue(summaryFinancial.current.total_revenue)}
                                        {...cardPercentage(summaryFinancial.variance?.total_revenue)}
                                    />
                                    <KeyStatTile
                                        size="primary"
                                        title={$t('Expenses')}
                                        {...cardValue(summaryFinancial.current.total_expenditure)}
                                        {...cardPercentage(summaryFinancial.variance?.total_expenditure, true)}
                                        reversed
                                    />
                                    <KeyStatTile
                                        size="primary"
                                        signed
                                        signValue={summaryFinancial.current.total_net}
                                        title={$t('Net')}
                                        {...cardValue(summaryFinancial.current.total_net)}
                                        {...cardPercentage(summaryFinancial.variance?.total_net)}
                                    />
                                </div>
                                {/* Secondary tier — Risks / Opportunities / AFC (smaller figures). */}
                                <div className="tw-grid tw-grid-cols-3 tw-gap-x-5 tw-gap-y-3 tw-mt-3">
                                    <KeyStatTile
                                        size="secondary"
                                        title={$t('Risks')}
                                        {...cardValue(summaryFinancial.current.total_risk)}
                                        {...cardPercentage(summaryFinancial.variance?.total_risk, true)}
                                        reversed
                                    />
                                    <KeyStatTile
                                        size="secondary"
                                        title={$t('Opportunities')}
                                        {...cardValue(summaryFinancial.current.total_opp)}
                                        {...cardPercentage(summaryFinancial.variance?.total_opp)}
                                    />
                                    <KeyStatTile
                                        size="secondary"
                                        signed
                                        signValue={summaryFinancial.current.total_afc}
                                        title={$t('AFC')}
                                        {...cardValue(summaryFinancial.current.total_afc)}
                                        {...cardPercentage(summaryFinancial.variance?.total_afc)}
                                    />
                                </div>
                            </>
                        )}
                        <div className="tw-mt-3">
                            {Object.keys(summaryFinancial).length > 0 && !!summaryFinancial.current && !!previousBudget && summaryFinancial.previous && (
                                <div className="inbud-table_wrapper">
                                    <table className="inbud-table tw-table-auto">
                                        <thead>
                                            <tr>
                                                <th className="tw-w-2/12"></th>
                                                <th>{$t('Revenue')}</th>
                                                <th>{$t('Expenditure')}</th>
                                                {showComparisonExtras && <th>{$t('Net')}</th>}
                                                {showComparisonExtras && (
                                                    <>
                                                        <th>{$t('Risks')}</th>
                                                        <th>{$t('Opportunities')}</th>
                                                        <th>{$t('AFC')}</th>
                                                    </>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td className="!tw-bg-white !tw-font-semibold">{previousBudget.name}</td>
                                                <ComparisonMoneyCell raw={summaryFinancial.previous.total_revenue} unit={unit} />
                                                <ComparisonMoneyCell raw={summaryFinancial.previous.total_expenditure} unit={unit} />
                                                {showComparisonExtras && <ComparisonMoneyCell raw={summaryFinancial.previous.total_net} unit={unit} />}
                                                {showComparisonExtras && (
                                                    <>
                                                        <ComparisonMoneyCell raw={summaryFinancial.previous.total_risk} unit={unit} />
                                                        <ComparisonMoneyCell raw={summaryFinancial.previous.total_opp} unit={unit} />
                                                        <ComparisonMoneyCell raw={summaryFinancial.previous.total_afc} unit={unit} />
                                                    </>
                                                )}
                                            </tr>
                                            <tr>
                                                <td className="!tw-bg-white !tw-font-semibold">{$t('Variance')}</td>
                                                <td className="inbud-numeric-cell">
                                                    {formatFigure(summaryFinancial.variance.total_revenue, formatToFixedMoneyWithLocale, { suffix: ' %' })}
                                                </td>
                                                <td className="inbud-numeric-cell">
                                                    {formatFigure(summaryFinancial.variance.total_expenditure, formatToFixedMoneyWithLocale, { suffix: ' %' })}
                                                </td>
                                                {showComparisonExtras && (
                                                    <td className="inbud-numeric-cell">
                                                        {formatFigure(summaryFinancial.variance.total_net, formatToFixedMoneyWithLocale, { suffix: ' %' })}
                                                    </td>
                                                )}
                                                {showComparisonExtras && (
                                                    <>
                                                        <td className="inbud-numeric-cell">
                                                            {formatFigure(summaryFinancial.variance.total_risk, formatToFixedMoneyWithLocale, { suffix: ' %' })}
                                                        </td>
                                                        <td className="inbud-numeric-cell">
                                                            {formatFigure(summaryFinancial.variance.total_opp, formatToFixedMoneyWithLocale, { suffix: ' %' })}
                                                        </td>
                                                        <td className="inbud-numeric-cell">
                                                            {formatFigure(summaryFinancial.variance.total_afc, formatToFixedMoneyWithLocale, { suffix: ' %' })}
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )
            )}
        </div>
    );
}

if (document.getElementById('KeyMetricsFinancialSummary')) {
    var root = document.getElementById('KeyMetricsFinancialSummary');
    ReactDOM.render(
        <App>
            <KeyMetricsFinancialSummary
                fetchUrl={fetchFinancialSummaryUrl}
                lockedBudgets={lockedBudgets}
                agencyMode={agencyMode}
                marginDisplayBasis={marginDisplayBasis}
            />
        </App>,
        root,
    );
}
